import * as Yup from 'yup';
import { stringToArray } from 'cron-converter';
import { PipelineSettingsFormFields } from '../../pipelines/PipelineSettings/interfaces/pipeline-settings-form-state';
import {
  CustomOptionType,
  EligibleFrequencyUnitsInCron,
  SyncFrequenciesFeatureOptions,
  SyncFrequencyTypes,
  SyncFrequencyWithCronFeatureOptions
} from '../../pipelines/interfaces/pipeline-settings-config';
import { DurationUnit } from '../interfaces/date';
import { convertMillisToNearestUnits, createDuration } from '../utils/date';
import { maxIntervalValidation, minIntervalValidation } from '../utils/cron';

export const pipelineSyncFrequencyValidationSchema = (
  syncFrequencyFeatureOptions: SyncFrequenciesFeatureOptions
) =>
  Yup.number().when(PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE, {
    is: syncFrequencyType => syncFrequencyType?.value !== SyncFrequencyTypes.CRON,
    then: Yup.number()
      .required('Pipeline sync frequency is required')
      .test('SyncFrequency', '', function validate(value) {
        const { custom_option: customOption, options: generalOptions } =
          syncFrequencyFeatureOptions;

        const doesMatchesGeneralOption = generalOptions.some(option => value === option.value);

        if (doesMatchesGeneralOption) {
          return true;
        }

        if (customOption.time_unit === DurationUnit.MILLISECONDS) {
          return validateCustomOptionWithMillisTimeUnit(
            customOption,
            value,
            this.parent[PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE].unit || ''
          );
        }

        return validateCustomOption(customOption, parseInt(value, 10));
      }),
    otherwise: Yup.number().nullable()
  });

const validateCustomOption = (customOption: CustomOptionType, value: number) => {
  const customMin = createDuration(customOption.min, customOption.time_unit as DurationUnit).as(
    'millisecond'
  );
  const customMax = createDuration(customOption.max, customOption.time_unit as DurationUnit).as(
    'millisecond'
  );

  if (value < customMin || value > customMax) {
    return new Yup.ValidationError(
      `Specify sync frequency in range of ${customOption.min} to ${
        customOption.max
      } ${customOption.time_unit.toLowerCase()}`,
      value,
      PipelineSettingsFormFields.SYNC_FREQUENCY
    );
  }
  return true;
};

export const pipelineSyncFrequencyWithCronValidationSchema = (
  syncFrequencyFeatureOptions: SyncFrequencyWithCronFeatureOptions
) =>
  Yup.string().when(PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE, {
    is: syncFrequencyType => syncFrequencyType?.value === SyncFrequencyTypes.CRON,
    then: Yup.string()
      .required('Pipeline sync frequency is required')
      .test('SyncFrequency', '', function validate(value) {
        return validateCronString(syncFrequencyFeatureOptions.custom_option, value);
      }),
    otherwise: Yup.string().nullable()
  });

const validateCustomOptionWithMillisTimeUnit = (
  customOption: CustomOptionType,
  value: number,
  unit: string
) => {
  const customMin = customOption.min;
  const customMax = customOption.max;

  let minWithUnits = convertMillisToNearestUnits(customMin);
  const maxWithUnits = convertMillisToNearestUnits(customMax);

  if (unit === EligibleFrequencyUnitsInCron.HOURS && minWithUnits.unit === DurationUnit.MINUTES) {
    minWithUnits = { value: 1, formattedUnit: 'hour', unit: DurationUnit.HOURS };
  }

  if (value < customMin || value > customMax) {
    return new Yup.ValidationError(
      `Specify sync frequency in range of ${minWithUnits.value} ${minWithUnits.formattedUnit} to ${maxWithUnits.value} ${maxWithUnits.formattedUnit}`,
      value,
      PipelineSettingsFormFields.SYNC_FREQUENCY
    );
  }

  return true;
};

const validateCronString = (cronOption: CustomOptionType, value: string) => {
  try {
    // to validate cron
    stringToArray(value);

    if (!minIntervalValidation(value, cronOption.min)) {
      const minWithUnits = convertMillisToNearestUnits(cronOption.min);
      return new Yup.ValidationError(
        `Minimum Interval Should be ${minWithUnits.value} ${minWithUnits.formattedUnit}`,
        value,
        PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON
      );
    }
    if (!maxIntervalValidation(value, cronOption.max)) {
      const maxWithUnits = convertMillisToNearestUnits(cronOption.max);
      return new Yup.ValidationError(
        `Maximum Interval Should be ${maxWithUnits.value} ${maxWithUnits.formattedUnit}`,
        value,
        PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON
      );
    }
    return true;
  } catch (error) {
    return new Yup.ValidationError(
      `Invalid CRON expression`,
      value,
      PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON
    );
  }
};
